import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, TextField, List, ListItemButton, ListItemText, Typography, Tabs, Tab, CircularProgress, useTheme, Pagination, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import api from '../../../../../utils/apiConfig';
import { useInventory } from '../../../../../contexts/InventoryContext';

export default function LoadFromInventoryDialog({ open, onClose, locationId, onCreateAd }) {
  const theme = useTheme();
  const [tab, setTab] = useState(0); // Start with inventory tab
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const { items, loadInventory, loading: inventoryLoading } = useInventory();
  const [localItems, setLocalItems] = useState([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [selectedImageUrls, setSelectedImageUrls] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // Allow changing items per page
  const [totalItems, setTotalItems] = useState(0);

  // Always fetch directly to ensure we get all items
  useEffect(() => {
    if (!open || !locationId) return;
    
    setLocalLoading(true);
    console.log('LoadFromInventoryDialog: Fetching inventory for locationId:', locationId);
    console.log('LoadFromInventoryDialog: API base URL:', process.env.REACT_APP_API_URL);
    
    api.get('/inventory', { 
      params: { 
        locationId, 
        page: 1, 
        limit: Math.min(itemsPerPage, 100), // Use itemsPerPage but respect server max of 100
        search: search || undefined
      } 
    })
      .then((res) => {
        console.log('LoadFromInventoryDialog: API response:', res.data);
        const list = Array.isArray(res.data?.data?.items) ? res.data.data.items
          : Array.isArray(res.data?.data) ? res.data.data
          : Array.isArray(res.data) ? res.data
          : [];
        console.log('LoadFromInventoryDialog: Parsed items:', list.length, 'items');
        setLocalItems(list);
        setTotalItems(res.data?.data?.pagination?.total || res.data?.data?.pagination?.totalItems || list.length);
      })
      .catch((e) => {
        console.error('LoadFromInventoryDialog: Direct inventory fetch failed', e);
        console.error('LoadFromInventoryDialog: Error details:', e.response?.data);
      })
      .finally(() => setLocalLoading(false));
  }, [open, locationId, itemsPerPage, search]); // Include itemsPerPage to refetch when it changes

  useEffect(() => {
    if (!open || !locationId) return;
    setLoadingTemplates(true);
    api.get('/photo-ads/assets', { params: { locationId, mimeType: 'application/vnd.dymen.photoads.template+json' } })
      .then((res) => {
        const list = res.data?.data || [];
        const filtered = list.filter((a) => a?.mimeType === 'application/vnd.dymen.photoads.template+json');
        setTemplates(filtered);
      })
      .catch((e) => console.error('Failed to load templates', e))
      .finally(() => setLoadingTemplates(false));
  }, [open, locationId]);

  useEffect(() => {
    if (!open) {
      setSelectedItemId(null);
      setSelectedTemplateId(null);
      setSearch('');
      setSelectedImageUrls([]);
      setTab(0); // Reset to inventory tab when dialog closes
      setCurrentPage(1); // Reset pagination when dialog closes
    }
  }, [open]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const sourceItems = localItems; // Always use local items to bypass context limits
  
  // Apply client-side filtering and pagination to local items
  const filteredItems = useMemo(() => {
    let filtered = sourceItems;
    if (search) {
      const s = search.toLowerCase();
      filtered = sourceItems.filter((it) =>
        String(it.name || it.title || it.sku || '').toLowerCase().includes(s)
        || String(it.description || '').toLowerCase().includes(s)
        || String(it.sku || '').toLowerCase().includes(s)
      );
    }
    // Apply client-side pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }, [sourceItems, search, currentPage, itemsPerPage]);

  // Calculate total pages from filtered results
  const totalPages = useMemo(() => {
    let filtered = sourceItems;
    if (search) {
      const s = search.toLowerCase();
      filtered = sourceItems.filter((it) =>
        String(it.name || it.title || it.sku || '').toLowerCase().includes(s)
        || String(it.description || '').toLowerCase().includes(s)
        || String(it.sku || '').toLowerCase().includes(s)
      );
    }
    return Math.ceil(filtered.length / itemsPerPage);
  }, [sourceItems, search, itemsPerPage]);

  const selectedItem = (sourceItems.find((i) => (i._id || i.id) === selectedItemId)) || null;
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;

  // Normalize item images/photos to URL list
  const itemImageUrls = useMemo(() => {
    const arr = (selectedItem?.images || selectedItem?.photos || []) || [];
    return arr.map((img) => typeof img === 'string' ? img : (img.url || img.secure_url)).filter(Boolean);
  }, [selectedItem]);

  // When a new item is selected, default-select all of its images
  useEffect(() => {
    setSelectedImageUrls(itemImageUrls);
  }, [itemImageUrls]);

  const toggleSelectImage = (url) => {
    setSelectedImageUrls((prev) => prev.includes(url) ? prev.filter(u => u !== url) : [...prev, url]);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Load from Inventory</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Inventory" />
          <Tab label="Templates" />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 1 }}
              />
              {localLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography>Loading…</Typography>
                </Box>
              ) : (
                <List dense>
                  {filteredItems.map((it) => (
                    <ListItemButton
                      key={it._id || it.id}
                      selected={selectedItemId === (it._id || it.id)}
                      onClick={() => {
                        setSelectedItemId(it._id || it.id);
                        // Don't auto-switch tabs - let user control navigation
                      }}
                    >
                      <ListItemText
                        primary={it.name || it.title || it.sku || 'Item'}
                        secondary={(it.category ? `${it.category} • ` : '') + (it.sku || '')}
                      />
                    </ListItemButton>
                  ))}
                  {filteredItems.length === 0 && (
                    <Typography color="text.secondary">No items found.</Typography>
                  )}
                </List>
              )}
              
              {/* Pagination and Items Per Page Controls */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Items per page</InputLabel>
                  <Select
                    value={itemsPerPage}
                    label="Items per page"
                    onChange={(e) => setItemsPerPage(e.target.value)}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Pagination
                    count={Math.max(totalPages, 1)}
                    page={currentPage}
                    onChange={(event, page) => setCurrentPage(page)}
                    color="primary"
                    size="small"
                  />
                  <Typography variant="caption">
                    Page {currentPage} of {totalPages} ({filteredItems.length} items)
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
              {selectedItem ? (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {itemImageUrls.length > 0 ? itemImageUrls.map((url, idx) => (
                      <Box
                        key={idx}
                        onClick={() => toggleSelectImage(url)}
                        sx={{
                          width: 90,
                          height: 90,
                          border: selectedImageUrls.includes(url) ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          background: theme.palette.background.default,
                        }}
                        title={selectedImageUrls.includes(url) ? 'Selected' : 'Click to select'}
                      >
                        <img src={url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )) : (
                      <Typography color="text.secondary">No images</Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Selected {selectedImageUrls.length} of {itemImageUrls.length} images. Order is applied by selection sequence.
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary">Select an item to preview images</Typography>
              )}
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            {loadingTemplates ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={20} />
                <Typography>Loading…</Typography>
              </Box>
            ) : (
              <List dense>
                {templates.map((t) => (
                  <ListItemButton
                    key={t.id}
                    selected={selectedTemplateId === t.id}
                    onClick={() => setSelectedTemplateId(t.id)}
                    sx={{ display: 'grid', gridTemplateColumns: '96px 1fr', alignItems: 'center', gap: 1 }}
                 >
                    <Box sx={{ width: 96, height: 72, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden', background: theme.palette.background.paper }}>
                      {t.thumbnailUrl ? (
                        <img
                          src={t.thumbnailUrl}
                          alt={t.name}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: 'transparent' }}
                          loading="lazy"
                        />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: 12 }}>
                          No thumbnail
                        </Box>
                      )}
                    </Box>
                    <ListItemText primary={t.name} secondary={`${t.width}×${t.height}`} />
                  </ListItemButton>
                ))}
                {templates.length === 0 && (
                  <Typography color="text.secondary">No templates yet. Use File → Save as Template.</Typography>
                )}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedItem || !selectedTemplate}
          onClick={() => selectedItem && selectedTemplate && onCreateAd?.(selectedItem, selectedTemplate, selectedImageUrls)}
        >
          Create Ad
        </Button>
      </DialogActions>
    </Dialog>
  );
}
